import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation, PapiExtendedDeviation } from '@wix/da-papi-types';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import FoldableTagList from '@wix/da-ds/pkg/TagList/FoldableTagList';
import DescViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/DeviationDescription';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import CollapsibleBlock from '../../../CollapsibleBlock';
import License from '../../../License';
import TextRenderer from '../../../TextRenderer/TextRenderer';
import DeviationAttributeCSV from '../_partials/DeviationAttributeCSV';
import StatBlock from '../_partials/StatBlock';
import Subjects from '../_partials/Subjects';
import DetailItemList from './DetailItemList';
import CreateWithDreamupBanner from '../_partials/CreateWithDreamupBanner';
import CreatedWithWixelBanner from '../_partials/CreatedWithWixelBanner';
import CreatedWithMuroDrawBanner from '../_partials/CreatedWithMuroDrawBanner';
import {
  getDeviationIsDrawSubmission,
  getDeviationIsWixelSubmission,
} from '../../../utils/deviations';

import defaultStyles from './Default.scss';

const DESCRIPTION_COLLAPSE_HEIGHT = 130;

export interface Props {
  deviation: PapiDeviation;
  withDividerBelowStats?: boolean;
}

export const ExtendedDetails: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  deviation: {
    deviationId,
    author,
    publishedTime,
    isDreamsofart,
    license,
    extended: {
      subjectTags,
      locationTag,
      tags,
      descriptionText,
      dreamsofart,
    } = {} as PapiExtendedDeviation,
  },
  withDividerBelowStats = true,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const descriptionRef = useRef<HTMLDivElement>(null);
  const [collapsedDescription, setCollapsedDescription] = useState<
    boolean | undefined
  >();

  const { isSubscribedButBlocked } = useSubscriptionTier(deviation);
  const isDescriptionTextShown =
    !!descriptionText?.html?.markup && !isSubscribedButBlocked;
  const areDetailItemsShown = !isSubscribedButBlocked && !isDreamsofart;

  const isOriginalsDeviation =
    !!deviation.originals || deviation.isOriginalsDelivery;
  const showCreatedWithWixelBanner = getDeviationIsWixelSubmission(deviation);
  const showCreatedWithMuroDrawBanner = getDeviationIsDrawSubmission(deviation);
  const showCreatedWithDreamupBanner =
    !showCreatedWithWixelBanner &&
    !showCreatedWithMuroDrawBanner &&
    deviation.extended?.showDreamupPromo &&
    !isOriginalsDeviation;

  // On mobile, collapse description block if height is over the threshold
  // deviationId is in deps to make sure we run this again if deviation changes due to duperbrowse
  useEffect(() => {
    if (
      isMobile &&
      (descriptionRef.current?.clientHeight ?? 0) > DESCRIPTION_COLLAPSE_HEIGHT
    ) {
      setCollapsedDescription(true);
    }
  }, [isMobile, deviationId]);

  return (
    <>
      <StatBlock
        deviation={deviation}
        className={defaultStyles['badges-and-stats']}
      />
      {withDividerBelowStats && <div className={defaultStyles['divider']} />}
      {tags && (
        <div className={defaultStyles['tags']}>
          <FoldableTagList
            tags={tags}
            moreLabel={
              <span
                title={t('common.showMore')}
                aria-label={t('common.showMore')}
              >
                {t('common.moreEllipsis')}
              </span>
            }
          />
        </div>
      )}
      <Subjects
        className={defaultStyles['subject']}
        subjectTags={subjectTags}
        locationTag={locationTag}
      />
      {isDescriptionTextShown && (
        <div
          className={cn(
            isMobile && defaultStyles['collapsible-description'],
            defaultStyles['description-wrapper'],
            collapsedDescription === true &&
              defaultStyles['collapsed-description'],
            collapsedDescription === false &&
              defaultStyles['expanded-description']
          )}
          id="description"
        >
          <VisuallyHidden>
            <Heading>{t('deviation.details.description')}</Heading>
          </VisuallyHidden>
          <TextRenderer
            key={`deviation-${deviationId}`}
            className={defaultStyles['description']}
            textContent={descriptionText}
            legacyTheme="extended-details"
            ref={descriptionRef}
            withSafetyCheckModal
            tipTapViewer={DescViewer}
            cookieType={MeasuredCookieType.DEVIATION_DESCRIPTION}
            defaultSize={{
              desktop: { width: 780, height: 800 },
              mobile: { width: 380, height: 400 },
            }}
          />

          {isMobile && collapsedDescription && (
            <div className={defaultStyles['expand-description']}>
              <PlainButtonOrLink
                onClick={() => setCollapsedDescription(false)}
                biData={BiData<DefaultBiEvent>({
                  evid: 2,
                  click_info: 'dev_page_description_expand',
                })}
                aria-controls={'description'}
                aria-expanded={collapsedDescription}
              >
                {t('deviation.details.showFullDescription')}
              </PlainButtonOrLink>
            </div>
          )}
        </div>
      )}
      {areDetailItemsShown && (
        <CollapsibleBlock
          buttonTextCollapsed={t('deviation.details.more')}
          buttonTextExpanded={t('deviation.details.less')}
          maxHeight={56}
          buttonClassName={defaultStyles['collapse-button']}
          className={defaultStyles['collapsible-block']}
          shutterBlockClassName={defaultStyles['collapsible-content']}
        >
          <div>
            <DetailItemList deviation={deviation} />
          </div>
        </CollapsibleBlock>
      )}

      <DeviationAttributeCSV deviation={deviation} />

      {(!deviation.extended?.showDreamupPromo || !dreamsofart) && (
        <div className={defaultStyles['license']}>
          <License
            license={license}
            author={author}
            publishedTime={publishedTime}
            showSymbol={false}
          />
        </div>
      )}

      <div className={defaultStyles['create-with-banners']}>
        {showCreatedWithWixelBanner && <CreatedWithWixelBanner />}
        {showCreatedWithMuroDrawBanner && <CreatedWithMuroDrawBanner />}
        {showCreatedWithDreamupBanner && (
          <CreateWithDreamupBanner
            deviation={deviation}
            className={defaultStyles['dreamup']}
            prompt={dreamsofart?.prompt}
            promptHidden={dreamsofart?.promptHidden}
            author={author}
            variationSource={dreamsofart?.parentDeviation}
          />
        )}
      </div>
    </>
  );
};
ExtendedDetails.displayName = 'ExtendedDetails';

export default ExtendedDetails;
